import {
    useMemo, useRef, useCallback,
    useState,
} from 'react'
import noop from 'lodash/noop'
import {
    GridCallbackDetails,
    GridFilterModel,
} from '@mui/x-data-grid-pro'

import useSkyDataGridLocalStorage from 'app/PoC/DataGridDemoPages/components/hooks/useSkyDataGridLocalStorage'

import {
    correctionFilterModel, getPredefinedDataFilter,
} from '../utils'
import {
    ExtendedGridColDef,
    ExtendedGridFilterModel,
    SkyDataGridLocalStorage,
} from '../SkyBaseDataGrid.types'

const usePersistedFilterModel = ({
    persistentKey,
    columns,
    onChange = noop,
    enablePersistence = true,
}:{
    persistentKey: string,
    columns: ExtendedGridColDef[],
    onChange?: ()=>void,
    enablePersistence?: boolean,
}): [GridFilterModel, (model: GridFilterModel, details: GridCallbackDetails) => void] => {
    const filterModelInitState = useMemo(() => {
        return {
            filterModel: {
                items: getPredefinedDataFilter(columns),
            },
        }
    }, [columns])

    // TODO-JM: Need enforce unique persistentKey for each table
    const {
        dataGridState,
        setDataGridState,
    } = useSkyDataGridLocalStorage<SkyDataGridLocalStorage>(persistentKey, filterModelInitState)

    const [
        localFilterModel,
        setLocalFilterModel,
    ] = useState<GridFilterModel>(filterModelInitState.filterModel)

    // For saving previous filter model to compare changes without re-renders
    const prevFilterModel = useRef<GridFilterModel>({
        items: [],
    })

    const onFilterModelChange = useCallback((model: ExtendedGridFilterModel, details: GridCallbackDetails<'filter'>) => {
        const correctedNewModel = correctionFilterModel(model, details, prevFilterModel.current)

        if (enablePersistence) {
            setDataGridState({
                filterModel: correctedNewModel,
            })
        } else {
            setLocalFilterModel(correctedNewModel)
        }

        prevFilterModel.current = correctedNewModel
        onChange()
    }, [
        onChange,
        setDataGridState,
        enablePersistence,
    ])

    const filterModel = useMemo(() => {
        if (enablePersistence) {
            return dataGridState?.filterModel || filterModelInitState.filterModel
        }
        return localFilterModel
    }, [
        dataGridState?.filterModel,
        filterModelInitState,
        enablePersistence,
        localFilterModel,
    ])

    return [
        filterModel,
        onFilterModelChange,
    ]
}

export default usePersistedFilterModel
